import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import { api, toastApiError, ApiRequestError } from '@/lib/api'
import {
  ReconciliationRunsResponseSchema,
  StartReconciliationResponseSchema,
  ReconciliationRunDetailResponseSchema,
  ReconciliationProposalSchema,
  type ReconciliationRunSummary,
  type ReconciliationRun,
  type ReconciliationProposal,
} from '@/lib/schemas'

// ===================
// Polling configuration
//
// Mirrors the extraction store's background-recovery poll: a 5s tick that
// stops on a terminal status, on nav-away (the store's currentBidId no longer
// matches), or when the run's poll handle is cleared. Exported mutable so
// tests can shorten the interval.
// ===================

export const RECONCILE_POLL_INTERVAL_MS = { value: 5_000 }
export const RECONCILE_POLL_MAX_DURATION_MS = { value: 15 * 60 * 1000 }

/** Non-terminal statuses that keep the poll running. */
const ACTIVE_STATUSES = new Set<ReconciliationRunSummary['status']>(['queued', 'running'])

/** Terminal = anything the worker won't advance past on its own. */
function isTerminalStatus(status: ReconciliationRunSummary['status']): boolean {
  return !ACTIVE_STATUSES.has(status)
}

// ===================
// Store
// ===================

/** Full run + proposals detail for the review surface (keyed by runId). */
export interface ReconciliationRunDetail {
  run: ReconciliationRun
  proposals: ReconciliationProposal[]
}

/** A user decision on a proposal — accept applies it, dismiss discards it. */
export type ProposalDecisionAction = 'accept' | 'dismiss'

/**
 * Why a proposal row could not be decided as requested. Rendered inline on the
 * row so the user re-reviews rather than silently losing the action.
 * - `stale` / `orphaned`: the target moved/vanished since the addendum ran — the
 *   detail was refreshed; the diff may now read differently.
 * - `apply_failed`: the accept was reverted server-side (the row is back to
 *   pending); retry.
 * - `skipped`: a decide-all pass skipped this row; `reason` carries the raw
 *   server reason (orphaned | stale | alreadyDecided | apply_failed).
 */
export type ProposalNoticeKind = 'stale' | 'orphaned' | 'apply_failed' | 'skipped'
export interface ProposalNotice {
  kind: ProposalNoticeKind
  reason?: string
}

/** Busy-key for a decide-all pass (whole-run when category is omitted). */
export function decideAllKey(runId: string, category?: string): string {
  return `${runId}::${category ?? '*'}`
}

// ── decide response shapes (see PR3 report §Response shapes) ──
interface DecideProposalResponse {
  proposal?: ReconciliationProposal
  applied?: boolean
  alreadyDecided?: boolean
}
interface DecideAllResponse {
  applied?: number
  dismissed?: number
  skipped?: Array<{ id: string; reason: string }>
}
interface ConflictBody {
  reason?: string
  proposal?: unknown
  target?: unknown
}

/** Read a reconciliation 409 conflict body off a thrown ApiRequestError. */
function getConflict(error: unknown): ConflictBody | null {
  if (
    error instanceof ApiRequestError &&
    error.statusCode === 409 &&
    error.details &&
    typeof error.details === 'object'
  ) {
    return error.details as ConflictBody
  }
  return null
}

function omitKey<T>(map: Record<string, T>, key: string): Record<string, T> {
  if (!(key in map)) return map
  const next = { ...map }
  delete next[key]
  return next
}

type StoreSet = (
  partial:
    | Partial<ReconciliationState>
    | ((state: ReconciliationState) => Partial<ReconciliationState>)
) => void
type StoreGet = () => ReconciliationState

/** Replace one proposal in a run's cached detail by id (no-op if not loaded). */
function replaceProposalInDetail(
  set: StoreSet,
  get: StoreGet,
  runId: string,
  updated: ReconciliationProposal
): void {
  const detail = get().runDetailByRun[runId]
  if (!detail) return
  set((state) => ({
    runDetailByRun: {
      ...state.runDetailByRun,
      [runId]: {
        ...detail,
        proposals: detail.proposals.map((p) => (p.id === updated.id ? updated : p)),
      },
    },
  }))
}

/**
 * Merge the fields of a reverted (apply_failed) proposal over the existing local
 * row, PRESERVING the local `target`. The 409 body's proposal is the raw DB row
 * — it lacks the live-target join, so a straight replace would drop `target` and
 * the view would render the row as orphaned (Accept hidden). Keep the local
 * `target` unless the body actually carries one.
 */
function mergeRevertedProposalInDetail(
  set: StoreSet,
  get: StoreGet,
  runId: string,
  reverted: ReconciliationProposal
): void {
  const detail = get().runDetailByRun[runId]
  if (!detail) return
  set((state) => ({
    runDetailByRun: {
      ...state.runDetailByRun,
      [runId]: {
        ...detail,
        proposals: detail.proposals.map((p) =>
          p.id === reverted.id
            ? { ...p, ...reverted, target: reverted.target ?? p.target }
            : p
        ),
      },
    },
  }))
}

/**
 * Optimistically move one decision from `pending` to `accepted`/`dismissed` on
 * the run's summary row in `runsByBid`, so badges/pills track partial progress
 * without waiting for `maybeSettle`'s list refresh. No-op if the run isn't in
 * the list (guarded), and pending never goes below zero.
 */
function bumpDecisionCountInSummary(
  set: StoreSet,
  get: StoreGet,
  bidId: string,
  runId: string,
  action: ProposalDecisionAction
): void {
  const runs = get().runsByBid[bidId]
  if (!runs?.some((r) => r.id === runId)) return
  set((state) => ({
    runsByBid: {
      ...state.runsByBid,
      [bidId]: (state.runsByBid[bidId] ?? []).map((r) => {
        if (r.id !== runId) return r
        const dc = r.decisionCounts
        if (dc.pending <= 0) return r
        return {
          ...r,
          decisionCounts: {
            pending: dc.pending - 1,
            accepted: action === 'accept' ? dc.accepted + 1 : dc.accepted,
            dismissed: action === 'dismiss' ? dc.dismissed + 1 : dc.dismissed,
          },
        }
      }),
    },
  }))
}

/**
 * Drop the run summary's `deadlineChange` highlight so the Overview warnstrip
 * clears the moment the deadline proposal is decided — without waiting for the
 * whole run to settle (a partial decide leaves other proposals pending, so
 * maybeSettle's list refresh doesn't fire). Called only when the decided
 * proposal is the key-date change on the submission-deadline row.
 */
function clearDeadlineHighlightInSummary(
  set: StoreSet,
  get: StoreGet,
  bidId: string,
  runId: string
): void {
  const runs = get().runsByBid[bidId]
  if (!runs?.some((r) => r.id === runId && r.highlights?.deadlineChange)) return
  set((state) => ({
    runsByBid: {
      ...state.runsByBid,
      [bidId]: (state.runsByBid[bidId] ?? []).map((r) => {
        if (r.id !== runId || !r.highlights?.deadlineChange) return r
        // Strip only deadlineChange, preserving any future highlight members.
        const { deadlineChange: _dropped, ...rest } = r.highlights
        return { ...r, highlights: rest }
      }),
    },
  }))
}

/** True when this proposal is the key-date change on the submission deadline. */
function isDeadlineProposal(proposal: ReconciliationProposal | undefined): boolean {
  return (
    proposal?.category === 'key_dates' &&
    proposal.kind === 'change' &&
    proposal.target?.isSubmissionDeadline === true
  )
}

/**
 * When a decision empties the run's pending pile, refresh the runs list so the
 * summary status settles (review → applied/dismissed) and the surface can show
 * its completion state. Guarded so a decision on a since-abandoned bid never
 * clobbers the currently-loaded bid's list.
 */
async function maybeSettle(get: StoreGet, bidId: string, runId: string): Promise<void> {
  const detail = get().runDetailByRun[runId]
  if (!detail) return
  if (detail.proposals.some((p) => p.status === 'pending')) return
  const cur = get().currentBidId
  if (cur !== null && cur !== bidId) return
  try {
    await get().loadRuns(bidId)
  } catch {
    // Ignore — the local detail already reflects the completed state.
  }
}

interface ReconciliationState {
  /** Runs keyed by bid — newest-first as returned by the list endpoint. */
  runsByBid: Record<string, ReconciliationRunSummary[]>
  /** runId → true while a background poll is in flight for that run. */
  pollHandles: Record<string, boolean>
  /** The bid whose runs are currently loaded — polls bail when this changes. */
  currentBidId: string | null
  /** Full detail (run + proposals) for the review surface, keyed by runId. */
  runDetailByRun: Record<string, ReconciliationRunDetail | undefined>
  /** runId → true while its detail fetch is in flight. */
  runDetailLoading: Record<string, boolean>
  /** runId → error message from the last failed detail fetch, else null. */
  runDetailError: Record<string, string | null>
  /** proposalId → true while a single-proposal decide is in flight (row guard). */
  decidingProposals: Record<string, boolean>
  /** decideAllKey(runId, category) → true while a decide-all pass is in flight. */
  decidingAll: Record<string, boolean>
  /** proposalId → inline notice (stale/orphaned/apply_failed/skipped). */
  proposalNotices: Record<string, ProposalNotice>

  // Actions
  loadRuns: (bidId: string) => Promise<void>
  startRun: (bidId: string, documentId: string) => Promise<string>
  pollRun: (bidId: string, runId: string) => void
  stopPoll: (runId: string) => void
  /** Fetch the full run + proposals for the review surface. */
  loadRunDetail: (bidId: string, runId: string) => Promise<void>
  /**
   * Decide a single proposal (accept/dismiss). On 200 updates the cached row +
   * settles the run when pending empties. On 409 stale/orphaned refreshes the
   * detail and flags the row for re-review; on 409 apply_failed restores the
   * reverted (pending) row from the body and toasts. Row-level busy-guarded.
   */
  decideProposal: (
    bidId: string,
    runId: string,
    proposalId: string,
    action: ProposalDecisionAction,
    /**
     * Re-baseline retry after a stale 409: the freshly-refreshed
     * `proposal.target.updatedAt`. Sent as the accept body's
     * `expectedTargetVersion` so the server guards against the version the user
     * just re-reviewed rather than the frozen proposal version.
     */
    expectedTargetVersion?: string
  ) => Promise<void>
  /**
   * Decide every pending proposal in the run (optionally scoped to a category).
   * Refreshes the detail, annotates skipped rows with an inline reason, and
   * settles the run when pending empties. Category-level busy-guarded.
   */
  decideAll: (
    bidId: string,
    runId: string,
    action: ProposalDecisionAction,
    category?: string
  ) => Promise<void>

  // Selectors
  getLatestRunForDocument: (
    bidId: string,
    documentId: string
  ) => ReconciliationRunSummary | null

  reset: () => void
}

export const useReconciliationStore = create<ReconciliationState>()(
  devtools(
    (set, get) => ({
      runsByBid: {},
      pollHandles: {},
      currentBidId: null,
      runDetailByRun: {},
      runDetailLoading: {},
      runDetailError: {},
      decidingProposals: {},
      decidingAll: {},
      proposalNotices: {},

      loadRuns: async (bidId: string) => {
        set({ currentBidId: bidId })
        const res = await api.get(`/bids/${bidId}/reconciliations`)
        const { runs } = ReconciliationRunsResponseSchema.parse(res)
        set((state) => ({
          runsByBid: { ...state.runsByBid, [bidId]: runs },
        }))
        // Resume polling for any run the worker is still processing.
        for (const run of runs) {
          if (ACTIVE_STATUSES.has(run.status) && !get().pollHandles[run.id]) {
            get().pollRun(bidId, run.id)
          }
        }
      },

      startRun: async (bidId: string, documentId: string) => {
        const res = await api.post(`/bids/${bidId}/reconciliations`, { documentId })
        const { runId } = StartReconciliationResponseSchema.parse(res)
        // Refresh the list so the new run appears immediately (and, if active,
        // gets a poll started by loadRuns). Best-effort — the poll below is the
        // authoritative progress source.
        try {
          await get().loadRuns(bidId)
        } catch {
          // Ignore — polling still drives the run to completion.
        }
        if (!get().pollHandles[runId]) {
          get().pollRun(bidId, runId)
        }
        return runId
      },

      pollRun: (bidId: string, runId: string) => {
        if (get().pollHandles[runId]) return
        set((state) => ({
          pollHandles: { ...state.pollHandles, [runId]: true },
        }))

        void (async () => {
          const startedAt = Date.now()
          while (Date.now() - startedAt < RECONCILE_POLL_MAX_DURATION_MS.value) {
            await new Promise((resolve) =>
              setTimeout(resolve, RECONCILE_POLL_INTERVAL_MS.value)
            )

            // Stopped explicitly, or the user switched bids.
            if (!get().pollHandles[runId]) return
            if (get().currentBidId !== bidId) {
              get().stopPoll(runId)
              return
            }

            let status: ReconciliationRunSummary['status'] | null = null
            try {
              const res = await api.get(`/bids/${bidId}/reconciliations/${runId}`)
              const { run } = ReconciliationRunDetailResponseSchema.parse(res)
              status = run.status
              // Merge live status into the list entry so the row pill advances.
              set((state) => ({
                runsByBid: {
                  ...state.runsByBid,
                  [bidId]: (state.runsByBid[bidId] ?? []).map((r) =>
                    r.id === runId
                      ? {
                          ...r,
                          status: run.status,
                          counts: run.counts ?? r.counts,
                          error: run.error ?? r.error,
                          updatedAt: run.updatedAt,
                        }
                      : r
                  ),
                },
              }))
            } catch {
              // Network flake — keep polling.
              continue
            }

            if (status && isTerminalStatus(status)) {
              // Refresh the list to pick up decisionCounts + highlights, which
              // the bare poll endpoint doesn't carry. Guard the refresh: loadRuns
              // unconditionally sets currentBidId, so a stale terminal resolve
              // after the user navigated to another bid would clobber the newer
              // bid's polls. Only refresh while this bid is still current.
              if (get().currentBidId === bidId) {
                try {
                  await get().loadRuns(bidId)
                } catch {
                  // Ignore — the merged status above is still correct.
                }
              }
              get().stopPoll(runId)
              return
            }
          }
          // Timed out — release the handle so a later action can re-poll.
          get().stopPoll(runId)
        })()
      },

      stopPoll: (runId: string) => {
        set((state) => {
          if (!(runId in state.pollHandles)) return state
          const next = { ...state.pollHandles }
          delete next[runId]
          return { pollHandles: next }
        })
      },

      loadRunDetail: async (bidId: string, runId: string) => {
        set((state) => ({
          runDetailLoading: { ...state.runDetailLoading, [runId]: true },
          runDetailError: { ...state.runDetailError, [runId]: null },
        }))
        try {
          const res = await api.get(`/bids/${bidId}/reconciliations/${runId}`)
          const detail = ReconciliationRunDetailResponseSchema.parse(res)
          set((state) => ({
            runDetailByRun: {
              ...state.runDetailByRun,
              [runId]: { run: detail.run, proposals: detail.proposals },
            },
            runDetailLoading: { ...state.runDetailLoading, [runId]: false },
          }))
        } catch (error) {
          set((state) => ({
            runDetailLoading: { ...state.runDetailLoading, [runId]: false },
            runDetailError: {
              ...state.runDetailError,
              [runId]: error instanceof Error ? error.message : 'Failed to load run',
            },
          }))
        }
      },

      decideProposal: async (
        bidId: string,
        runId: string,
        proposalId: string,
        action: ProposalDecisionAction,
        expectedTargetVersion?: string
      ) => {
        // Row-level in-flight guard — ignore repeat clicks while deciding.
        if (get().decidingProposals[proposalId]) return
        // Capture the pre-decision row (full target incl. isSubmissionDeadline)
        // so we can clear the Overview warnstrip if this is the deadline change.
        const decidedRow = get().runDetailByRun[runId]?.proposals.find(
          (p) => p.id === proposalId
        )
        set((state) => ({
          decidingProposals: { ...state.decidingProposals, [proposalId]: true },
          // A fresh decision supersedes any prior notice on this row.
          proposalNotices: omitKey(state.proposalNotices, proposalId),
        }))

        try {
          const res = await api.patch<DecideProposalResponse>(
            `/bids/${bidId}/reconciliations/${runId}/proposals/${proposalId}`,
            {
              action,
              ...(expectedTargetVersion ? { expectedTargetVersion } : {}),
            }
          )

          if (res.alreadyDecided) {
            // Server state diverged from ours (e.g. a colleague decided it) —
            // refetch to reconcile the row, and clear the warnstrip if the
            // already-decided row was the deadline change.
            await get().loadRunDetail(bidId, runId)
            if (isDeadlineProposal(decidedRow)) {
              clearDeadlineHighlightInSummary(set, get, bidId, runId)
            }
          } else if (res.proposal) {
            // Validate the row before caching it — a malformed body must never
            // poison the cache; fall back to a full refetch on parse failure.
            const parsed = ReconciliationProposalSchema.safeParse(res.proposal)
            if (parsed.success) {
              replaceProposalInDetail(set, get, runId, parsed.data)
            } else {
              await get().loadRunDetail(bidId, runId)
            }
            // Optimistically advance the summary counts so badges/pills track
            // partial progress before maybeSettle's list refresh lands.
            bumpDecisionCountInSummary(set, get, bidId, runId, action)
            // Deciding the deadline change clears the Overview warnstrip now,
            // not on full-run settle (other proposals may stay pending).
            if (isDeadlineProposal(decidedRow)) {
              clearDeadlineHighlightInSummary(set, get, bidId, runId)
            }
          }
          await maybeSettle(get, bidId, runId)
        } catch (error) {
          const conflict = getConflict(error)
          if (conflict?.reason === 'stale' || conflict?.reason === 'orphaned') {
            // Target moved/vanished — refresh the detail and flag the row so the
            // user re-reviews the (now different) diff instead of losing the click.
            set((state) => ({
              proposalNotices: {
                ...state.proposalNotices,
                [proposalId]: { kind: conflict.reason as ProposalNoticeKind },
              },
            }))
            await get().loadRunDetail(bidId, runId)
          } else if (conflict?.reason === 'apply_failed') {
            // The accept was reverted server-side — the 409 body carries the
            // reverted (pending) proposal, but as a raw DB row WITHOUT the live
            // `target` join. Merge its fields over the existing row and preserve
            // the local `target` so the row stays acceptable (not orphaned).
            const parsed = ReconciliationProposalSchema.safeParse(conflict.proposal)
            if (parsed.success) mergeRevertedProposalInDetail(set, get, runId, parsed.data)
            set((state) => ({
              proposalNotices: {
                ...state.proposalNotices,
                [proposalId]: { kind: 'apply_failed' },
              },
            }))
            toastApiError(
              new Error("Couldn't apply this change — it was reverted. Please try again."),
              'Failed to apply change'
            )
          } else {
            toastApiError(error, 'Failed to update this change.')
          }
        } finally {
          set((state) => ({
            decidingProposals: omitKey(state.decidingProposals, proposalId),
          }))
        }
      },

      decideAll: async (
        bidId: string,
        runId: string,
        action: ProposalDecisionAction,
        category?: string
      ) => {
        const key = decideAllKey(runId, category)
        // Category-level in-flight guard.
        if (get().decidingAll[key]) return
        set((state) => ({
          decidingAll: { ...state.decidingAll, [key]: true },
        }))
        // Clear notices on the pending rows this pass will touch.
        const scoped = (get().runDetailByRun[runId]?.proposals ?? []).filter(
          (p) => p.status === 'pending' && (!category || p.category === category)
        )
        if (scoped.length > 0) {
          set((state) => {
            let notices = state.proposalNotices
            for (const p of scoped) notices = omitKey(notices, p.id)
            return { proposalNotices: notices }
          })
        }

        try {
          const res = await api.post<DecideAllResponse>(
            `/bids/${bidId}/reconciliations/${runId}/decide-all`,
            { action, ...(category ? { category } : {}) }
          )
          // Refresh the detail so decided rows drop out of pending; skipped rows
          // stay pending and get an inline reason below.
          await get().loadRunDetail(bidId, runId)
          // Clear the Overview warnstrip only if this pass touched the deadline
          // change AND it's no longer pending (a skipped/stale row stays pending).
          if (scoped.some(isDeadlineProposal)) {
            const stillPending = (get().runDetailByRun[runId]?.proposals ?? []).some(
              (p) => p.status === 'pending' && isDeadlineProposal(p)
            )
            if (!stillPending) clearDeadlineHighlightInSummary(set, get, bidId, runId)
          }
          if (Array.isArray(res.skipped) && res.skipped.length > 0) {
            set((state) => {
              const notices = { ...state.proposalNotices }
              for (const s of res.skipped ?? []) {
                notices[s.id] = { kind: 'skipped', reason: s.reason }
              }
              return { proposalNotices: notices }
            })
          }
          await maybeSettle(get, bidId, runId)
        } catch (error) {
          toastApiError(
            error,
            action === 'accept' ? 'Failed to accept changes.' : 'Failed to dismiss changes.'
          )
        } finally {
          set((state) => ({ decidingAll: omitKey(state.decidingAll, key) }))
        }
      },

      getLatestRunForDocument: (bidId: string, documentId: string) => {
        const runs = get().runsByBid[bidId] ?? []
        const forDoc = runs.filter((r) => r.documentId === documentId)
        if (forDoc.length === 0) return null
        return forDoc.reduce((latest, r) =>
          r.createdAt > latest.createdAt ? r : latest
        )
      },

      reset: () => {
        set({
          runsByBid: {},
          pollHandles: {},
          currentBidId: null,
          runDetailByRun: {},
          runDetailLoading: {},
          runDetailError: {},
          decidingProposals: {},
          decidingAll: {},
          proposalNotices: {},
        })
      },
    }),
    { name: 'reconciliation-store' }
  )
)
